import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';
import { useDebouncedCallback } from '@mantine/hooks';
import { getReferenceString } from '@medplum/core';
import { Practitioner, Reference } from '@medplum/fhirtypes';
import { TimelineFilterType } from '@bariendo/lib/constants';
import { formatDate } from '@/services/date';
import { FilterType } from '@/components/Timeline/TimelineFilters';

interface UseTimelineFiltersParams {
  activeFilter: FilterType;
  searchTerm: string;
  startDate: Date | null;
  endDate: Date | null;
  selectedProvider: Reference<Practitioner> | null;
}

interface UseTimelineFiltersResult {
  localSearchTerm: string;
  updateUrlParam: (paramName: string, value: string | null, shouldDelete?: (val: string | null) => boolean) => void;
  handleFilterChange: (filter: FilterType) => void;
  handleSearchChange: (search: string) => void;
  handleStartDateChange: (date: Date | null) => void;
  handleEndDateChange: (date: Date | null) => void;
  handleProviderChange: (provider: Reference<Practitioner> | null) => void;
  hasActiveFilters: boolean;
}

// Format provider reference for URL parameter
const formatProviderForUrl = (provider: Reference<Practitioner> | null): string | null => {
  return provider ? getReferenceString(provider) || null : null;
};

export function useTimelineFilters(params: UseTimelineFiltersParams): UseTimelineFiltersResult {
  const { activeFilter, searchTerm, startDate, endDate, selectedProvider } = params;
  const [searchParams, setSearchParams] = useSearchParams();
  const [localSearchTerm, setLocalSearchTerm] = useState(searchTerm);

  // Sync local search term with URL param
  useEffect(() => {
    setLocalSearchTerm(searchTerm);
  }, [searchTerm]);

  // Generic function to update URL parameters
  const updateUrlParam = useCallback(
    (paramName: string, value: string | null, shouldDelete: (val: string | null) => boolean = (val) => !val) => {
      const newSearchParams = new URLSearchParams(searchParams);
      if (shouldDelete(value)) {
        newSearchParams.delete(paramName);
      } else {
        newSearchParams.set(paramName, value!);
      }
      setSearchParams(newSearchParams);
    },
    [searchParams, setSearchParams]
  );

  // Handle filter changes by updating URL
  const handleFilterChange = useCallback(
    (filter: FilterType) => {
      updateUrlParam('type', filter === TimelineFilterType.ALL ? null : filter);
    },
    [updateUrlParam]
  );

  const handleChangeSearchUrlParam = useDebouncedCallback((search: string) => {
    updateUrlParam('search', search.trim(), (val) => val === '');
  }, 800);

  // Handle search term changes by updating URL
  const handleSearchChange = useCallback(
    (search: string) => {
      setLocalSearchTerm(search);
      handleChangeSearchUrlParam(search);
    },
    [handleChangeSearchUrlParam]
  );

  // Handle start date changes by updating URL
  const handleStartDateChange = useCallback(
    (date: Date | null) => {
      updateUrlParam('startDate', date ? formatDate(date, 'YYYY-MM-DD') : null);
    },
    [updateUrlParam]
  );

  // Handle end date changes by updating URL
  const handleEndDateChange = useCallback(
    (date: Date | null) => {
      updateUrlParam('endDate', date ? formatDate(date, 'YYYY-MM-DD') : null);
    },
    [updateUrlParam]
  );

  // Handle provider changes by updating URL
  const handleProviderChange = useCallback(
    (provider: Reference<Practitioner> | null) => {
      updateUrlParam('provider', formatProviderForUrl(provider));
    },
    [updateUrlParam]
  );

  const hasActiveFilters = useMemo(() => {
    return (
      activeFilter !== TimelineFilterType.ALL ||
      searchTerm.trim() !== '' ||
      selectedProvider !== null ||
      startDate !== null ||
      endDate !== null
    );
  }, [activeFilter, searchTerm, selectedProvider, startDate, endDate]);

  return {
    localSearchTerm,
    updateUrlParam,
    handleFilterChange,
    handleSearchChange,
    handleStartDateChange,
    handleEndDateChange,
    handleProviderChange,
    hasActiveFilters,
  };
}
